// 通用分页控件：智能页码窗口 + 每页条数 + 跳页（列表页共用，配合服务端分页）
interface Props {
  page: number;
  pageSize: number;
  total: number;
  sizes?: number[];
  unit?: string; // 计数单位，如「张」「条」
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
}

/** 页码序列：首尾恒显，当前页 ±2，缺口用 '…' 占位 */
export function pageWindow(current: number, last: number): Array<number | '…'> {
  if (last <= 1) return [1];
  const nums = new Set<number>([1, last]);
  for (let p = current - 2; p <= current + 2; p++) {
    if (p >= 1 && p <= last) nums.add(p);
  }
  const sorted = [...nums].sort((a, b) => a - b);
  const out: Array<number | '…'> = [];
  sorted.forEach((n, i) => {
    if (i > 0 && n - sorted[i - 1] > 1) out.push('…');
    out.push(n);
  });
  return out;
}

const NAV = 'h-8 min-w-8 px-2 rounded-lg text-sm font-medium transition-colors disabled:text-slate-300 disabled:cursor-not-allowed';

export default function Pagination({ page, pageSize, total, sizes = [20, 50, 100], unit = '条', onPage, onPageSize }: Props) {
  const last = Math.max(1, Math.ceil(total / pageSize));
  const cur = Math.min(page, last);
  const from = total === 0 ? 0 : (cur - 1) * pageSize + 1;
  const to = Math.min(cur * pageSize, total);
  const go = (p: number) => { if (p >= 1 && p <= last && p !== cur) onPage(p); };

  return (
    <div className="flex items-center gap-3 flex-wrap mt-4 px-1">
      <div className="text-xs text-slate-500">
        共 <span className="font-mono font-semibold text-slate-700">{total}</span> {unit}
        {total > 0 && <span className="text-slate-400">　显示 {from}–{to}</span>}
      </div>

      <label className="flex items-center gap-1.5 text-xs text-slate-500">
        每页
        <select
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          className="border border-slate-300 rounded-lg px-2 py-1 text-sm bg-white"
        >
          {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>

      <div className="flex items-center gap-1 ml-auto">
        <button className={`${NAV} hover:bg-slate-100 text-slate-500`} disabled={cur <= 1} onClick={() => go(1)} title="首页">«</button>
        <button className={`${NAV} hover:bg-slate-100 text-slate-500`} disabled={cur <= 1} onClick={() => go(cur - 1)} title="上一页">‹</button>
        {pageWindow(cur, last).map((p, i) =>
          p === '…'
            ? <span key={`e${i}`} className="px-1 text-slate-400 text-sm">…</span>
            : (
              <button
                key={p}
                onClick={() => go(p)}
                aria-current={p === cur ? 'page' : undefined}
                className={`${NAV} ${p === cur ? 'bg-brand-500 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                {p}
              </button>
            )
        )}
        <button className={`${NAV} hover:bg-slate-100 text-slate-500`} disabled={cur >= last} onClick={() => go(cur + 1)} title="下一页">›</button>
        <button className={`${NAV} hover:bg-slate-100 text-slate-500`} disabled={cur >= last} onClick={() => go(last)} title="末页">»</button>
      </div>

      {last > 1 && (
        <form
          className="flex items-center gap-1.5 text-xs text-slate-500"
          onSubmit={(e) => {
            e.preventDefault();
            const el = (e.currentTarget.elements.namedItem('jump') as HTMLInputElement | null);
            const n = Number(el?.value);
            if (Number.isFinite(n)) { go(Math.min(Math.max(1, Math.round(n)), last)); if (el) el.value = ''; }
          }}
        >
          跳至
          <input
            name="jump"
            type="number"
            min={1}
            max={last}
            className="w-16 border border-slate-300 rounded-lg px-2 py-1 text-sm"
            aria-label="跳转到指定页"
          />
          <button type="submit" className={`${NAV} text-brand-600 hover:bg-brand-50`}>GO</button>
        </form>
      )}
    </div>
  );
}
